import React, { useState, createContext, useContext, useCallback, useEffect } from 'react';
import type { User, AuthContextType } from '../types';
import useApi from "../hooks/useApi";
import ILogin from "../models/ILogin";

import { useCookies } from "react-cookie";
import { useParams } from "react-router-dom";
import Guid from "../models/Guid";

const dateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
const GuidFormat = /^(\{){0,1}[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}(\}){0,1}$/;

function reviver(key: any, value: string) {
  if (dateFormat.test(value)) {
    return new Date(value);
  }

  if (GuidFormat.test(value)) {
    return new Guid(value);
  }

  return value;
}

function AuthProvider(props: React.PropsWithChildren) {
  const { id } = useParams();
  const [{ user }, setCookie, removeCookie] = useCookies(['user'], { doNotParse: true });
  const [loading, setLoading] = useState(false);
  const [userParsed, setUserParsed] = useState<User | undefined>(user ? JSON.parse(user, reviver) : undefined);

  const { postData } = useApi<ILogin>("usager/login");

  const signIn = useCallback(async (username: string, password: string) => {
    await setLoading(true);
    try {

      const result = await postData(
        {
          CodeUsager: username,
          Password: password
        },
        undefined,
        false,
        undefined,
        {
          "Authorization": "TOKEN " + null,
          "Accept-Language": 'FR',
          "Accept": "application/json",
          "Access-Control-Allow-Headers": "Content-Type",
          "Content-Type": "application/json",
        });

      if (!result || !result.usager) {
        return {
          isOk: false,
          data: undefined,
          message: 'Réponse de connexion invalide.',
        };
      }

      const nextUser: User = {
        email: `${result.usager.nom} ${result.usager.prenom}`,
        token: result.apiKey,
        nom: result.usager.nom,
        prenom: result.usager.prenom,
        idUsager: new Guid(result.usager.idUsager)
      };
      const cookieSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
      setCookie('user', nextUser, { maxAge: 60 * 60 * 24 * 31, secure: cookieSecure, path: `/${id}` });
      setUserParsed(nextUser);

      return {
        isOk: true,
        data: nextUser,
        message: ''
      };
    } catch (e) {
      if (e instanceof Error)
        return {
          isOk: false,
          data: undefined,
          message: e.message
        };
    } finally {
      await setLoading(false);
    }

    return {
      isOk: false,
    };
  }, []);

  const signOut = useCallback(() => {
    removeCookie('user', { path: `/${id}` });
    setUserParsed(undefined);
  }, [id, removeCookie]);


  return (
    <AuthContext.Provider value={{ user: userParsed, signIn, signOut, loading }} {...props} />
  );
}

const AuthContext = createContext<AuthContextType>({ loading: false } as AuthContextType);
const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth }
